"use client"

import { useEffect, useRef } from "react";

import benz from "@/assets/Dashboard/benz-brand.svg";
import fox from "@/assets/Dashboard/fox-brand.svg";
import canadian from "@/assets/Dashboard/canadian.png";
import kron from "@/assets/Dashboard/kron-brand.svg";
import morning from "@/assets/Dashboard/morning-brand.svg";
import prop from "@/assets/Dashboard/prop-brand.svg";

import { useAtom } from "jotai";
import { themeAtom } from "@/store/atoms";

export default function BrandingSection() {
  const [_theme] = useAtom(themeAtom);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll functionality
  useEffect(() => {
    if (scrollContainerRef.current) {
      const scrollContainer = scrollContainerRef.current;
      let scrollPosition = scrollContainer.scrollWidth - scrollContainer.clientWidth;
      const scrollSpeed = 1; // pixels per frame
      const scrollInterval = 30; // milliseconds between scroll updates
      let intervalId: NodeJS.Timeout | null = null;

      const autoScroll = () => {
        if (scrollContainer) {
          scrollPosition -= scrollSpeed;

          // Reset scroll position when reaching the beginning
          if (scrollPosition <= 0) {
            scrollPosition = scrollContainer.scrollWidth - scrollContainer.clientWidth;
          }

          scrollContainer.scrollLeft = scrollPosition;
        }
      };

      const startScroll = () => {
        if (intervalId) clearInterval(intervalId);
        intervalId = setInterval(autoScroll, scrollInterval);
      };

      const stopScroll = () => {
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      };

      // Start initial scroll
      startScroll();

      // Pause auto-scroll on hover
      const handleMouseEnter = () => stopScroll();
      const handleMouseLeave = () => startScroll();

      // Handle page visibility changes to prevent speed issues
      const handleVisibilityChange = () => {
        if (document.hidden) {
          stopScroll();
        } else {
          // Recalculate position when page becomes visible
          scrollPosition = scrollContainer.scrollLeft;
          startScroll();
        }
      };

      // Handle page focus changes
      const handleFocus = () => {
        scrollPosition = scrollContainer.scrollLeft;
        startScroll();
      };

      const handleBlur = () => stopScroll();

      scrollContainer.addEventListener('mouseenter', handleMouseEnter);
      scrollContainer.addEventListener('mouseleave', handleMouseLeave);
      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('focus', handleFocus);
      window.addEventListener('blur', handleBlur);

      return () => {
        stopScroll();
        scrollContainer.removeEventListener('mouseenter', handleMouseEnter);
        scrollContainer.removeEventListener('mouseleave', handleMouseLeave);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('focus', handleFocus);
        window.removeEventListener('blur', handleBlur);
      };
    }
  }, []);

  return (
    <div className="w-full md:mt-8">
      <div className=" font-nexa">
        <h3 className=" text-center text-2xl md:text-5xl leading-tight font-normal pt-2 pb-5">
            We are<span className="italic text-gradient font-light px-1">featured in</span>
          </h3>
        <div className="relative flex items-center z-50 max-w-[1240px] mx-auto mt-10">
          
        <div className="pointer-events-none absolute inset-y-0 left-0 w-[284px] bg-gradient-to-r from-black/50 to-transparent z-10" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-[284px] bg-gradient-to-l from-black/50 to-transparent z-10" />
          <div
            ref={scrollContainerRef}
            className="flex relative justify-start items-center w-full gap-x-20 overflow-x-scroll overflow-y-hidden scrollbar-hide"
          >
           
            {/* Multiple sets of logos for seamless scrolling */}
            <BrandingImages />
            <BrandingImages />
            <BrandingImages />
            <BrandingImages />
            <BrandingImages />
            <BrandingImages />
          </div>
        </div>
      </div>
    </div>
  );
}

const BrandingImages = () => {
  return (
    <>
     
      <a
        href="https://www.kron4.com/business/press-releases/globenewswire/9504270/urfx-io-reports-surge-in-trader-growth-as-demand-rises-for-instant-funding-and-ai-powered-trading/"
        target="_blank"
        rel="noopener noreferrer"
      >
        <img src={kron} alt="kron logo" className="brightness-100 -z-20 min-w-[80px] md:min-w-[120px] h-auto" />
      </a>
      
      
      <a
        href="https://propinsider.com/tag/urfx-io/"
        target="_blank"
        rel="noopener noreferrer"
      >
        <img src={prop} alt="prop logo" className="brightness-100 -z-20 min-w-[80px] md:min-w-[120px] h-auto" />
      </a>
      <a
        href="https://fox4kc.com/business/press-releases/globenewswire/9504270/urfx-io-reports-surge-in-trader-growth-as-demand-rises-for-instant-funding-and-ai-powered-trading/"
        target="_blank"
        rel="noopener noreferrer"
      >
        <img src={fox} alt="fox logo" className="brightness-100 -z-20 min-w-[80px] md:min-w-[120px] h-auto" />
      </a>
      <a
        href="https://www.morningstar.com/news/globe-newswire/9504270/urfxio-reports-surge-in-trader-growth-as-demand-rises-for-instant-funding-and-ai-powered-trading"
        target="_blank"
        rel="noopener noreferrer"
      >
        <img
          src={morning}
          alt="morning logo"
          className="brightness-100 -z-20 min-w-[80px] md:min-w-[120px] h-auto"
        />
      </a>
      <a
        href="https://www.benzinga.com/pressreleases/25/07/g46772870/urfx-io-reports-surge-in-trader-growth-as-demand-rises-for-instant-funding-and-ai-powered-trading"
        target="_blank"
        rel="noopener noreferrer"
      >
        <img src={benz} alt="benz logo" className="brightness-100 -z-20 min-w-[80px] md:min-w-[120px] h-auto" />
      </a>
      <a
        href="https://www.globenewswire.com/news-release/2025/07/31/3125342/0/en/URFX-io-Reports-Surge-in-Trader-Growth-as-Demand-Rises-for-Instant-Funding-and-AI-Powered-Trading.html"
        target="_blank"
        rel="noopener noreferrer"
      >
        <img
        width={184}
          src={canadian}
          alt="canadian logo"
          className="brightness-100 -z-20 min-w-[80px] md:min-w-[184px] h-auto"
        />
      </a>
      
    </>
  );
};